import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../Common/Modal';
import PersonaForm from './PersonaForm';
import { Persona } from '../../types';
import { Button } from '../Common/button';

interface PersonaFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<Persona>) => void;
  initialData?: Partial<Persona>;
  isLoading?: boolean;
  error?: string | null;
}

const PersonaFormModal: React.FC<PersonaFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isLoading = false,
  error = null,
}) => {
  const { t } = useTranslation();
  const formRef = useRef<{ submit: () => void }>(null);

  const modalTitle = initialData?.id ? t('persona.form.editPersona') : t('persona.form.addPersona');

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={modalTitle}
      className="max-w-4xl"
      hideDefaultFooter={true}
      overlayClassName="bg-black/80"
    >
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">{t('global.error')}!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      )}
      <div className="mt-4">
        <PersonaForm
          ref={formRef}
          onSubmit={onSubmit}
          initialData={initialData}
        />
      </div>
      
      {/* Custom Footer with Close and Submit buttons */}
      <div className="px-4 py-3 border-t border-border flex justify-end gap-3">
        <Button onClick={onClose} variant="outline" disabled={isLoading}>
          {t('global.close')}
        </Button>
        <Button onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? t('global.saving') : t('persona.form.addPersona')}
        </Button>
      </div>
    </Modal>
  );
};

export default PersonaFormModal;
